import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useRouteLoaderData } from '@remix-run/react';

import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import remixI18n from '~/i18next.server';
import type { Community, CurrentCommunityMember } from '~/interfaces/community';
import type { Subscription } from '~/interfaces/subscriptions';
import type { RootLoaderData } from '~/root';
import { getCommunityLoaderData } from '~/services/api/community/getCommunityLoaderData.server';
import getReferrals from '~/services/api/subscription/getReferrals.server';
import getSubscriptionProfile from '~/services/api/subscription/getSubsciptionProfile.server';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import { publicationFromMatches } from '~/utils/publicationFromLoaderData';
import redirectWithToast from '~/utils/redirectWithToast/index.server';

import Page from './_components/v2/Page';
import ReadOnlyPage from './_components/v2/ReadOnlyPage';
import { getSiteFontsData } from './_utils/withSiteFonts';

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const publication = publicationFromMatches(matches);
  const metaTitle = data?.metaTitle;

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !metaTitle,
    overrides: {
      robots: 'noindex',
      title: metaTitle,
      description: `Help the ${publication.name} community grow by sharing your referral link and earning rewards.`,
    },
  });
};

export const loader: LoaderFunction = async ({ request, params, context }) => {
  const { session } = await loaderSession(request);
  const subscriberId = params?.subscriberId;
  const url = new URL(request.url);

  const sitePromise = context.getSite(request, session);
  const communityDataPromise = getCommunityLoaderData(request, session, sitePromise);
  const [site, referrals, subscription, [community, currentCommunityMember]] = await Promise.all([
    sitePromise,
    getReferrals(request, session, subscriberId),
    getSubscriptionProfile(request, session, subscriberId),
    communityDataPromise,
  ]);
  const publication = site.publication;
  const locale = publication?.language || 'en';

  // TODO(@manpreet): Should we validate subscription exists here like other routes?
  // Context: Other routes (manage.tsx, billing.tsx, preferences.tsx) throw 404 if !subscription
  // This route returns subscription regardless, which can be undefined.
  // This causes 41k+ Sentry errors in useCurrentPlan hook downstream.
  // For now, useCurrentPlan has defensive handling, but should we throw here instead?
  // Questions to answer:
  // - Can you view referrals without a subscription?
  // - Is undefined subscription a valid state for this page?
  // - Should we show 404 or a different error page?

  if (referrals) {
    const [t, { fontsCss: renderableFontsCss, adobeFontUrls }] = await Promise.all([
      remixI18n.getFixedT(locale, 'common'),
      getSiteFontsData(site),
    ]);

    return json({
      subscription,
      community,
      currentCommunityMember,
      requestUrl: url.href,
      referrals: referrals.referrals,
      rewards: referrals.rewards,
      referralCount: referrals.referral_count,
      referralLink: referrals.referral_link,
      metaTitle: t('meta.titles.referrals'),
      subscriberId,
      renderableFontsCss,
      adobeFontUrls,
    });
  }

  const t = await remixI18n.getFixedT(locale, 'common');

  return redirect(
    redirectWithToast({
      baseUrl: '/',
      toast: 'error',
      message: `${t('errors.referrals')} ${t('errors.try_later')}`,
    }),
  );
};

interface ReferralsLoaderData {
  subscription: Subscription;
  community: Community | null;
  currentCommunityMember: CurrentCommunityMember | null;
  referrals: any;
  rewards: any;
  referralCount: number;
  referralLink: string;
  subscriberId: string;
}

const Referrals = () => {
  const {
    site,
    isLoggedIn,
    isFetchingSubscription,
    subscription: loggedInUserSubscription,
  } = useRouteLoaderData('root') as RootLoaderData;
  const publication = site?.publication;

  const { subscription, subscriberId, community, currentCommunityMember } = useTypedLoaderData<ReferralsLoaderData>();

  if (!site || !publication || !subscription) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }

  if (isFetchingSubscription) {
    return null;
  }

  const isReadOnly = isLoggedIn && subscriberId !== loggedInUserSubscription?.subscriber_id;
  if (isReadOnly) {
    return <ReadOnlyPage site={site} loggedInSubscriberId={loggedInUserSubscription?.subscriber_id || ''} />;
  }

  return (
    <Page
      site={site}
      publication={publication}
      subscriberId={subscriberId}
      subscription={subscription}
      isLoggedIn={isLoggedIn}
      community={community}
      currentCommunityMember={currentCommunityMember}
    />
  );
};

export default Referrals;
